import { BaseMonitor } from '@exodus/asset-lib'
import { getAssetAddresses, isRpcBalanceAsset } from '@exodus/ethereum-lib'
import lodash from 'lodash'

import { fromHexToString } from '../number-utils.js'
import {
  checkPendingTransactions,
  excludeUnchangedTokenBalances,
  getAllLogItemsByAsset,
  getDeriveDataNeededForTick,
  getDeriveTransactionsToCheck,
  getHistoryFromServer,
  getLogItemsFromServerTx,
} from './monitor-utils/index.js'
import {
  enableWSUpdates,
  subscribeToGasPriceNotifications,
  subscribeToWSNotifications,
} from './ws-updates.js'

const { isEmpty } = lodash

// The base ethereum monitor class handles listening for, fetching,
// formatting, and populating-to-state all ETH/ETC/ERC20 transactions.

export class EthereumMonitor extends BaseMonitor {
  constructor({ server, config, webSocketEnabled = true, ...args }) {
    super(args)
    this.server = server
    this.config = { GAS_PRICE_FROM_WEBSOCKET: true, ...config }
    this.deriveDataNeededForTick = getDeriveDataNeededForTick(this.aci)
    this.deriveTransactionsToCheck = getDeriveTransactionsToCheck({
      getTxLog: (...args) => this.aci.getTxLog(...args),
    })
    this.subscribedToWalletAccountNotificationsMap = new Map()
    this.addHook('before-start', (...args) => this.beforeStart(...args))
    this.addHook('after-stop', (...args) => this.afterStop(...args))
    this.subscribedToGasPriceMap = new Map()
    this._webSocketEnabled = webSocketEnabled
  }

  setServer(config = {}) {
    const uri = config?.serverv1 || this.server.defaultUri

    if (uri === this.server.getURL()) {
      return
    }

    this.server.setURL(uri)
  }

  async deriveData({ assetSource, tokens }) {
    const { asset: assetName, walletAccount } = assetSource

    const { ourWalletAddress, currentAccountState, minimumConfirmations } =
      await this.deriveDataNeededForTick({ assetName, walletAccount })
    const transactionsToCheck = await this.deriveTransactionsToCheck({
      assetName,
      walletAccount,
      tokens,
      ourWalletAddress,
    })

    return {
      ourWalletAddress,
      currentAccountState,
      minimumConfirmations,
      ...transactionsToCheck,
    }
  }

  // eslint-disable-next-line no-undef
  async checkPendingTransactions(params) {
    const { pendingTransactionsToCheck, pendingTransactionsGroupedByAddressAndNonce } =
      checkPendingTransactions(params)
    const txsToRemove = []
    const {
      assetSource: { walletAccount },
    } = params

    const updateTx = (tx, asset, { isTxConfirmed, remove }) => {
      if (remove) {
        txsToRemove.push({ tx, assetSource: { asset, walletAccount } })
      } else {
        params.logItemsByAsset[asset].push({
          ...tx,
          ...(isTxConfirmed ? { confirmations: 1 } : { dropped: true, error: 'Dropped' }),
        })
      }

      // in case this is an ETH fee tx that has associated ERC20 send txs
      const promises = tx.tokens.map(async (assetName) => {
        const tokenTxSet = await this.aci.getTxLog({ assetName, walletAccount })
        if (remove) {
          txsToRemove.push({
            tx: tokenTxSet.get(tx.txId),
            assetSource: { asset: assetName, walletAccount },
          })
        } else if (tokenTxSet && tokenTxSet.has(tx.txId)) {
          params.logItemsByAsset[assetName].push({
            ...tokenTxSet.get(tx.txId),
            ...(isTxConfirmed ? { confirmations: 1 } : { dropped: true, error: 'Dropped' }),
          })
        }
      })

      return Promise.all(promises)
    }

    for (const { tx, assetName, replaced = false } of Object.values(
      pendingTransactionsGroupedByAddressAndNonce
    )) {
      if (replaced) {
        await updateTx(tx, assetName, { remove: true })
        delete pendingTransactionsToCheck[tx.txId]
      }
    }

    for (const { tx, assetName } of Object.values(pendingTransactionsToCheck)) {
      if (params.refresh) {
        await updateTx(tx, assetName, { remove: true })
      } else {
        // FIXME: Fetching unconfirmed txs from node helps to avoid
        // flagging on-chain confirmed txs as dropped in the wallet.
        // Once the real bug is found, remove this logic
        const rpcTx = await this.server.getTransactionByHash(tx.txId)
        const isTxConfirmed = !!rpcTx?.blockNumber
        this.logger.info(`tx ${tx.txId} confirmed: ${isTxConfirmed}`)
        await updateTx(tx, assetName, { isTxConfirmed })
      }
    }

    return {
      txsToRemove,
    }
  }

  async tick({ refresh, walletAccount }) {
    await this._subscribeToWSNotifications() // checks if it's a new walletAccount

    const assets = await this.aci.getAssetsForNetwork({ baseAssetName: this.asset.name })
    const tokens = Object.values(assets).filter((asset) => asset.baseAsset.name !== asset.name)
    const tokensByAddress = tokens.reduce((map, token) => {
      const addresses = getAssetAddresses(token)
      for (const address of addresses) map.set(address.toLowerCase(), token)
      return map
    }, new Map())
    const assetSource = { asset: this.asset.name, walletAccount }

    const derivedData = await this.deriveData({ assetSource, tokens })
    const derivedIndex = derivedData.currentAccountState?.index

    const { allTransactionsFromServer, index } = await getHistoryFromServer({
      server: this.server,
      ourWalletAddress: derivedData.ourWalletAddress,
      minimumConfirmations: derivedData.minimumConfirmations,
      index: refresh ? 0 : derivedIndex ?? 0,
    })
    const hasNewIndex = !derivedIndex || index > derivedIndex

    const logItemsByAsset = getAllLogItemsByAsset({
      getLogItemsFromServerTx,
      ourWalletAddress: derivedData.ourWalletAddress,
      allTransactionsFromServer,
      asset: this.asset,
      tokensByAddress,
      assets,
    })

    const { txsToRemove } = await this.checkPendingTransactions({
      txlist: allTransactionsFromServer,
      assetSource,
      refresh,
      logItemsByAsset,
      asset: this.asset,
      ...derivedData,
    })

    const accountState = await this.getNewAccountState({
      tokens,
      currentTokenBalances: derivedData.currentAccountState?.tokenBalances,
      ourWalletAddress: derivedData.ourWalletAddress,
    })

    const newData = { ...accountState, ...(hasNewIndex && { index }) }
    await this.updateAccountState({ newData, walletAccount })
    await this.removeFromTxLog(txsToRemove)
    await this.updateTxLogByAsset({ logItemsByAsset, walletAccount, refresh })

    if (refresh || hasNewIndex) {
      const unknownTokenAddresses = this.getUnknownTokenAddresses({
        transactions: allTransactionsFromServer,
        tokensByAddress,
      })
      if (unknownTokenAddresses.length > 0) {
        this.emit('unknown-tokens', unknownTokenAddresses)
      }
    }
  }

  async updateGasPrice({ gasPrice, baseFeePerGas }) {
    try {
      const feeConfig = {
        gasPrice: `${parseInt(gasPrice, 16)} wei`,
      }
      if (baseFeePerGas) {
        feeConfig.baseFeePerGas = `${parseInt(baseFeePerGas, 16)} wei`
      }

      this.logger.debug(
        `Update ${this.asset.name} gas price: ${feeConfig.gasPrice}, baseFeePerGas: ${feeConfig.baseFeePerGas}`
      )
      await this.aci.updateFeeConfig({ assetName: this.asset.name, feeConfig })
    } catch (e) {
      this.logger.warn('error updating gasPrice', e)
    }
  }

  async getNewAccountState({ tokens, currentTokenBalances, ourWalletAddress }) {
    const asset = this.asset
    const newAccountState = {}
    const server = this.server
    if (isRpcBalanceAsset(asset)) {
      const result = await server.getBalanceProxied(ourWalletAddress)
      const balance = fromHexToString(result)
      newAccountState.balance = asset.currency.baseUnit(balance)
    }

    const tokenBalancePairs = await Promise.all(
      tokens
        .filter((token) => isRpcBalanceAsset(token) && token.contract.address)
        .map(async (token) => {
          const { confirmed } = await server.balanceOf(ourWalletAddress, token.contract.address)
          const value = token.currency.baseUnit(confirmed[token.contract.address] || 0)
          return [token.name, value]
        })
    )

    const tokenBalances = excludeUnchangedTokenBalances(currentTokenBalances, tokenBalancePairs)
    if (!isEmpty(tokenBalances)) newAccountState.tokenBalances = tokenBalances
    return newAccountState
  }

  async getReceiveAddressesByWalletAccount() {
    const walletAccounts = await this.aci.getWalletAccounts({ assetName: this.asset.name })
    const addressesByAccount = {}
    for (const walletAccount of walletAccounts) {
      addressesByAccount[walletAccount] = await this.aci.getReceiveAddresses({
        assetName: this.asset.name,
        walletAccount,
        useCache: true,
      })
    }

    return addressesByAccount
  }

  async _subscribeToWSNotifications() {
    if (this._webSocketEnabled === false) return

    const addressesByWalletAccount = await this.getReceiveAddressesByWalletAccount()
    subscribeToWSNotifications({
      addressesByWalletAccount,
      tick: (...args) => this.tick(...args),
      server: this.server,
    })
  }

  getUnknownTokenAddresses({ transactions, tokensByAddress }) {
    const set = transactions.reduce((acc, txn) => {
      const transfers = txn.erc20 || []
      transfers.forEach((transfer) => {
        const addr = transfer.address.toLowerCase()
        if (!tokensByAddress.has(addr)) {
          acc.add(addr)
        }
      })
      return acc
    }, new Set())
    return [...set]
  }

  async beforeStart() {
    await this._enableWSUpdates()
  }

  async _enableWSUpdates() {
    if (this._webSocketEnabled === false) return

    const addressesByWalletAccount = await this.getReceiveAddressesByWalletAccount()
    enableWSUpdates({
      interval: this.interval,
      server: this.server,
      timer: this.timer,
      tick: (...args) => this.tick(...args),
      tickWalletAccounts: () => this.tickWalletAccounts(),
      addressesByWalletAccount,
      beforeStart: true,
    })

    if (this.config.GAS_PRICE_FROM_WEBSOCKET) {
      subscribeToGasPriceNotifications({
        server: this.server,
        updateGasPrice: (newGasPrice) => this.updateGasPrice(newGasPrice),
        subscribedToGasPriceMap: this.subscribedToGasPriceMap,
      })
    }
  }

  async afterStop() {
    await this.server.stop()
  }
}
